import { z } from 'zod';
import { Brand } from '../../models/stores/Brand';

class BrandService {
  private static instance: BrandService;
  private defaultLocale: string = 'de_CH';
  private brands: Brand[] = z.array(Brand).parse([
    {
      id: 3,
      brandId: 3,
      translations: [
        {
          id: 1,
          locale: 'en',
          name: 'kybun',
          webName: 'kybun',
          description: 'The kybun Shop offers innovative Swiss health solutions – from the original kybun shoes with elastic, springy air-cushion soles to standing mats that make your workday more dynamic and healthier.'
        },
        {
          id: 1,
          locale: 'de_CH',
          name: 'kybun',
          webName: 'kybun',
          description: 'Der kybun Shop bietet Ihnen innovative Schweizer Gesundheitslösungen – von den original kybun Schuhen mit elastisch-federnder Luftkissensohle bis hin zu Stehmatten, die Ihren Arbeitsalltag dynamischer und gesünder machen.'
        }
      ],
      logoUrl: '../assets/logos/kybun-logo.png',
      pinUrl: '../assets/pins/kybun-marker.png',
      priority: 3
    },
    {
      id: 6,
      brandId: 6,
      translations: [
        {
          id: 1,
          locale: 'en',
          name: 'Joya',
          webName: 'joya',
          description: 'In the Joya Shop, you will find a large selection of stylish, comfortable health shoes. They promote natural foot movement and ensure a particularly soft, joint-friendly walking experience.'
        },
        {
          id: 1,
          locale: 'de_CH',
          name: 'Joya',
          webName: 'joya',
          description: 'Im Joya Shop finden Sie eine grosse Auswahl an stilvollen, bequemen Gesundheitsschuhen. Sie fördern die natürliche Abrollbewegung und sorgen für ein besonders weiches, gelenkschonendes Geherlebnis.'
        }
      ],
      logoUrl: '../assets/logos/joya-logo.png',
      pinUrl: '../assets/pins/joya-marker.png',
      priority: 2
    },
    {
      id: 7,
      brandId: 7,
      translations: [
        {
          id: 1,
          locale: 'en',
          name: 'kybun Joya',
          webName: 'kybunjoya',
          description: 'Discover the perfect combination of expertise, modern foot analysis, and the largest selection of shoes and health products at the kybun Joya Shop – individually tailored to your needs.'
        },
        {
          id: 1,
          locale: 'de_CH',
          name: 'kybun Joya',
          webName: 'kybunjoya',
          description: 'Entdecken Sie im kybun Joya Shop die perfekte Verbindung aus Fachkompetenz, moderner Fussanalyse und der grössten Auswahl an Schuhen und Gesundheitsprodukten – individuell abgestimmt auf Ihre Bedürfnisse.'
        }
      ],
      logoUrl: '../assets/logos/kybun-joya-logo.png',
      pinUrl: '../assets/pins/kybun-joya-marker.png',
      priority: 4
    },
    {
      id: 8,
      brandId: 8,
      translations: [
        {
          id: 1,
          locale: 'en',
          name: 'Kandahar',
          webName: 'kandahar',
          description: 'This specialist retailer presents the traditional brand Kandahar – elegant, sustainable winter boots from Switzerland, crafted with uncompromising quality. Each pair is handmade in Switzerland and combines timeless design with functional warmth.'
        },
        {
          id: 1,
          locale: 'de_CH',
          name: 'Kandahar',
          webName: 'kandahar',
          description: 'Dieser Fachhändler präsentiert Ihnen die Traditionsmarke Kandahar – elegante, nachhaltige Winterschuhe aus der Schweiz, gefertigt in kompromissloser Qualität. Jedes Paar entsteht in Handarbeit in der Schweiz und vereint zeitloses Design mit funktionaler Wärme.'
        }
      ],
      logoUrl: '../assets/logos/kandahar-logo.png',
      pinUrl: '../assets/pins/kandahar-marker.png',
      priority: 1
    }
  ]);

  private constructor(defaultLocale: string) {
    this.defaultLocale = defaultLocale;
  }

  public init(enabledBrands: string[]): void {
    if (enabledBrands && enabledBrands.length > 0) {
      const lowercasedAllowedBrandNames = enabledBrands.map(name => name.toLowerCase());
      this.brands = this.brands.filter(brand => {
        if (brand.translations) {
          return Object.values(brand.translations).some(translation =>
            lowercasedAllowedBrandNames.includes(
              translation.webName
              ? translation.webName.toLowerCase()
              : translation.name.toLowerCase())
          );
        }
        return false;
      });
    }
  }

  public static getInstance(defaultLocale: string): BrandService {
    if (!BrandService.instance) {
      BrandService.instance = new BrandService(defaultLocale);
    }
    return BrandService.instance;
  }

  public getDefaultLocale() {
    return this.defaultLocale;
  }
  public setDefaultLocale(locale: string) {
    this.defaultLocale = locale;
  }

  public getBrands(): Brand[] {
    return this.brands;
  }

  public setBrands(brands: Brand[]): void {
    this.brands = brands;
  }

  public addBrand(brand: Brand): void {
    this.brands = [...this.brands, brand];
  }

  public getBrand(brandId: number): Brand | null {
    return this.brands.find(brand => brand.brandId === brandId) || null;
  }

  public getName(brandId: number, locale: string = this.defaultLocale): string | null {
    const brand = this.brands.find(brand => brand.brandId === brandId);
    if (!brand) return null;

    if (!locale) locale = this.defaultLocale;

    if (!brand.translations) return null;

    const translation = brand.translations[locale];
    if (translation) return translation.name;

    return null;
  }

  public getIdWithName(name: string): number | null {
    const lowercasedName = name.toLowerCase();
    const brand = this.brands.find(brand => {
      if (!brand.translations) return false;
      return Object.values(brand.translations).some(translation =>
        translation.name.toLowerCase() === lowercasedName
      );
    });
    return brand ? brand.brandId : null;
  }

  public getIdWithWebName(webName: string): number | null {
    const lowercasedWebName = webName.toLowerCase();
    const brand = this.brands.find(brand => {
      if (!brand.translations) return false;
      return Object.values(brand.translations).some(translation =>
        translation.webName && translation.webName.toLowerCase() === lowercasedWebName
      );
    });
    return brand ? brand.brandId : null;
  }

  public getLogoUrl(brandId: number): string | null {
    const brand = this.brands.find(brand => brand.brandId === brandId);
    if (!brand) return null;

    return brand.logoUrl || null;
  }

  public getPinUrl(brandId: number): string | null {
    const brand = this.brands.find(brand => brand.brandId === brandId);
    if (!brand) return null;

    return brand.pinUrl || null;
  }

  public getHighestPriorityBrand(brands: number[]): Brand | null {
    return brands
      .map(brandId => this.getBrand(brandId))
      .filter(brand => !!brand)
      .sort((a, b) => (b!.priority || 0) - (a!.priority || 0))[0] || null;
  }

  public getAllBrandsOrderedByPriority(): Brand[] {
    return this.brands
      .filter(brand => !!brand)
      .sort((a, b) => (b!.priority || 0) - (a!.priority || 0));
  }

  public getLogosOrderedByPriority(brands: number[]): string[] {
    return brands
      .map(brandId => this.getBrand(brandId))
      .filter(brand => !!brand)
      .sort((a, b) => (b!.priority || 0) - (a!.priority || 0))
      .map(brand => brand!.logoUrl)
      .filter(logoUrl => logoUrl !== null && logoUrl !== undefined);
  }
}

export const brandService = BrandService.getInstance('de_CH');
